// services/placement.service.ts

export type PlacementRecordStatus = "confirmed" | "offer_pending" | "rejected";
export type JobType = "Full-time" | "Part-time" | "Contract" | "Internship";

export interface PlacementRecordType {
  _id: string;
  role: string; // Job Role
  company: string; // Vendor ID
  students: string[]; // Array of Student IDs
  departments: string[]; // Array of Dept IDs
  batches: string[]; // Array of Batch IDs
  package: number; // annual package in USD/LPA
  jobType: JobType;
  placementDate: string; // ISO date string
  joiningDate?: string; // ISO date string
  status: PlacementRecordStatus;
  eligibilityCriteria?: string;
  jobDescription?: string;
  requirements?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PlacementCreateType {
  role: string;
  company: string;
  students: string[];
  departments: string[];
  batches: string[];
  package: number;
  jobType: JobType;
  placementDate: string;
  joiningDate?: string;
  status: PlacementRecordStatus;
  eligibilityCriteria?: string;
  jobDescription?: string;
  requirements?: string;
}

export interface PlacementMetaType {
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export type PlacementParamsType = {
  search?: string;
  status?: PlacementRecordStatus | "all";
  page?: number;
  limit?: number;
};

// ─── Dummy Data ───────────────────────────────────────────────────────────────

const DUMMY_PLACEMENTS: PlacementRecordType[] = [
  {
    _id: "1",
    role: "Software Engineer",
    company: "1", // Microsoft
    students: ["1"], // Emma Watson
    departments: ["IT"],
    batches: ["3"], // IT 2021-2025
    package: 115000,
    jobType: "Full-time",
    placementDate: "2025-01-09",
    joiningDate: "2025-06-01",
    status: "confirmed",
    eligibilityCriteria: "Min 8.0 CGPA",
    jobDescription: "Software development role in Azure team.",
    requirements: "C#, .NET, Azure",
    createdAt: "2025-01-09T00:00:00.000Z",
    updatedAt: "2025-01-09T00:00:00.000Z",
  },
  {
    _id: "2",
    role: "SDE-I",
    company: "3", // Amazon
    students: ["4"], // Sophia Brown
    departments: ["CSE"],
    batches: ["1"], // CSE 2021-2025
    package: 110000,
    jobType: "Full-time",
    placementDate: "2025-01-08",
    joiningDate: "2025-07-01",
    status: "confirmed",
    eligibilityCriteria: "Min 7.5 CGPA",
    jobDescription: "SDE role in AWS.",
    requirements: "Java, AWS, DSA",
    createdAt: "2025-01-08T00:00:00.000Z",
    updatedAt: "2025-01-08T00:00:00.000Z",
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const simulateDelay = (ms = 400) =>
  new Promise((resolve) => setTimeout(resolve, ms));

const applyFilters = (
  records: PlacementRecordType[],
  filters: PlacementParamsType
): { data: PlacementRecordType[]; total: number } => {
  let result = [...records];

  if (filters.search) {
    const q = filters.search.toLowerCase();
    result = result.filter(
      (r) =>
        r.role.toLowerCase().includes(q) ||
        // r.company.toLowerCase().includes(q) || // Needs lookup
        r.jobDescription?.toLowerCase().includes(q)
    );
  }

  if (filters.status && filters.status !== "all") {
    result = result.filter((r) => r.status === filters.status);
  }

  const total = result.length;
  const page = filters.page ?? 1;
  const limit = filters.limit ?? 10;
  result = result.slice((page - 1) * limit, page * limit);

  return { data: result, total };
};

// ─── Service Functions ────────────────────────────────────────────────────────

export const fetchAllPlacements = async (filters: PlacementParamsType) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.get("/placements/", { params: filters });
  const limit = filters.limit ?? 10;
  const page = filters.page ?? 1;
  const { data, total } = applyFilters(DUMMY_PLACEMENTS, filters);
  return {
    placements: data,
    metaData: {
      pagination: { total, page, limit, totalPages: Math.ceil(total / limit) },
    } as PlacementMetaType,
  };
};

export const fetchPlacementById = async (_id: string) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.get(`/placements/${_id}`);
  const record = DUMMY_PLACEMENTS.find((r) => r._id === _id);
  if (!record) throw new Error("Placement record not found");
  return record;
};

export const createPlacement = async (data: PlacementCreateType) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.post("/placements/", data);
  const newRecord: PlacementRecordType = {
    ...data,
    _id: String(Date.now()),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  DUMMY_PLACEMENTS.push(newRecord);
  return newRecord;
};

export const updatePlacement = async (
  _id: string,
  data: PlacementCreateType
) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.put(`/placements/${_id}`, data);
  const index = DUMMY_PLACEMENTS.findIndex((r) => r._id === _id);
  if (index === -1) throw new Error("Placement record not found");
  DUMMY_PLACEMENTS[index] = {
    ...DUMMY_PLACEMENTS[index],
    ...data,
    updatedAt: new Date().toISOString(),
  };
  return DUMMY_PLACEMENTS[index];
};

export const deletePlacement = async (_id: string) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.delete(`/placements/${_id}`);
  const index = DUMMY_PLACEMENTS.findIndex((r) => r._id === _id);
  if (index === -1) throw new Error("Placement record not found");
  const [deleted] = DUMMY_PLACEMENTS.splice(index, 1);
  return deleted;
};
